import type {
  AthenaAuthCallOptions,
  AthenaAuthFetchCompatibleInput,
  AthenaAuthSignInResponse,
  AthenaAuthStatusResponse,
  AthenaDeleteUserCallbackRequest,
  AthenaDeleteUserRequest,
  AthenaDeleteUserResponse,
  AthenaEmailSignInRequest,
  AthenaEmailSignUpRequest,
  AthenaUpdateUserRequest,
  AthenaUsernameSignInRequest,
  InternalAthenaAuthModule,
} from "../types.ts";
import type { AuthSessionMutationController } from "./session-mutations.ts";
import type { AuthTransport } from "./transport.ts";

export function createUserClientModule(input: {
  mutations: AuthSessionMutationController;
  transport: AuthTransport;
}) {
  const { mutations, transport } = input;
  const { resolvedConfig } = transport;

  const signInEmail = async (
    input: AthenaEmailSignInRequest & AthenaAuthFetchCompatibleInput,
    options?: AthenaAuthCallOptions
  ) =>
    mutations.applyAuthMutationToSessionStore(
      await transport.postGeneric<AthenaAuthSignInResponse>(
        "/sign-in/email",
        input,
        options
      )
    );

  const signInUsername = async (
    input: AthenaUsernameSignInRequest & AthenaAuthFetchCompatibleInput,
    options?: AthenaAuthCallOptions
  ) =>
    mutations.applyAuthMutationToSessionStore(
      await transport.postGeneric<AthenaAuthSignInResponse>(
        "/sign-in/username",
        input,
        options
      )
    );

  const signUpEmail = async (
    input: AthenaEmailSignUpRequest & AthenaAuthFetchCompatibleInput,
    options?: AthenaAuthCallOptions
  ) =>
    mutations.applyAuthMutationToSessionStore(
      await transport.postGeneric<AthenaAuthSignInResponse>(
        "/sign-up/email",
        input,
        options
      )
    );

  const updateUser = async (
    input: AthenaUpdateUserRequest & AthenaAuthFetchCompatibleInput,
    options?: AthenaAuthCallOptions
  ) =>
    mutations.applyAuthMutationToSessionStore(
      await transport.postGeneric<AthenaAuthStatusResponse>(
        "/update-user",
        input,
        options
      ),
      { refreshIfMissing: true }
    );

  const deleteUser = async (
    input?: AthenaDeleteUserRequest & AthenaAuthFetchCompatibleInput,
    options?: AthenaAuthCallOptions
  ) => {
    const { payload, fetchOptions } = transport.extractFetchOptions(input);
    const mergedOptions = transport.mergeCallOptions(fetchOptions, options);
    const result = await transport.callAuthEndpoint<AthenaDeleteUserResponse>(
      resolvedConfig,
      { endpoint: "/delete-user", method: "POST" },
      payload ?? {},
      undefined,
      mergedOptions
    );
    if (result.ok) {
      mutations.sessionStore.invalidate("revoke");
    }
    return result;
  };

  const deleteUserCallback = (
    input?: AthenaDeleteUserCallbackRequest & AthenaAuthFetchCompatibleInput,
    options?: AthenaAuthCallOptions
  ) => {
    const { payload, fetchOptions } = transport.extractFetchOptions(input);
    const mergedOptions = transport.mergeCallOptions(fetchOptions, options);
    const query = (payload ?? {}) as AthenaDeleteUserCallbackRequest;
    return transport.callAuthEndpoint<AthenaDeleteUserResponse>(
      resolvedConfig,
      { endpoint: "/delete-user/callback", method: "GET" },
      undefined,
      {
        callbackURL: query.callbackURL,
        token: query.token,
      },
      mergedOptions
    );
  };

  const deleteUserVerify: InternalAthenaAuthModule["auth"]["deleteUserVerify"] =
    (input, options) =>
      transport.getWithQuery("/delete-user/verify", input, options);

  return {
    deleteUser,
    deleteUserCallback,
    deleteUserVerify,
    signInEmail,
    signInUsername,
    signUpEmail,
    updateUser,
  };
}
